import Link from "next/link";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { SALES_URL, SIGNUP_URL } from "@/lib/links";

export type ButtonVariant = "primary" | "secondary" | "quiet" | "onDark" | "onDarkSecondary";
export type ButtonSize = "sm" | "md" | "lg";

/** Class string for anything that should look like a button (links, submit inputs). */
export function buttonClass(variant: ButtonVariant = "primary", size: ButtonSize = "md", className?: string) {
  return cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-btn font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50",
    size === "sm" && "h-9 px-3.5 text-small",
    size === "md" && "h-11 px-5 text-[0.9375rem]",
    size === "lg" && "h-12 px-6 text-base",
    variant === "primary" && "bg-navy text-white hover:bg-navy-3",
    variant === "secondary" && "border border-navy/25 bg-white text-navy hover:border-navy hover:bg-fog-2",
    variant === "quiet" && "h-auto px-0 text-navy underline decoration-line underline-offset-4 hover:text-tan-ink hover:decoration-tan",
    variant === "onDark" && "bg-white text-navy hover:bg-fog",
    variant === "onDarkSecondary" && "border border-white/35 text-white hover:border-white hover:bg-white/5",
    className,
  );
}

type ButtonProps = {
  href?: string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  external?: boolean;
  onClick?: () => void;
  type?: "button" | "submit";
  disabled?: boolean;
};

export function Button({
  href,
  children,
  variant = "primary",
  size = "md",
  className,
  external,
  onClick,
  type = "button",
  disabled,
}: ButtonProps) {
  const cls = buttonClass(variant, size, className);
  if (href) {
    const isExternal = external ?? href.startsWith("http");
    return (
      <Link href={href} className={cls} {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
        {children}
      </Link>
    );
  }
  return (
    <button type={type} onClick={onClick} className={cls} disabled={disabled}>
      {children}
    </button>
  );
}

/** The two calls to action every hero and close uses: Start free + Contact sales. */
export function CtaPair({
  onDark = false,
  size = "lg",
  className,
  primaryLabel = "Start free",
  secondaryLabel = "Contact sales",
  secondaryHref = SALES_URL,
}: {
  onDark?: boolean;
  size?: ButtonSize;
  className?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
}) {
  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      <Button href={SIGNUP_URL} size={size} variant={onDark ? "onDark" : "primary"}>
        {primaryLabel}
      </Button>
      <Button href={secondaryHref} size={size} variant={onDark ? "onDarkSecondary" : "secondary"}>
        {secondaryLabel}
      </Button>
    </div>
  );
}
